import React, { useCallback, useContext, useMemo, useState } from "react";
// import { Points } from "@components/VaultTabs";
import PointsV2 from "@components/V2/Vault/Common/Points";
import Claims from "@components/V2/Vault/Common/Claims";
import Holdings from "@components/V2/Vault/Common/Holdings";
import VaultAprReports, {
  CMSAllocation,
} from "@components/V2/Vault/Common/VaultAprReports";
import { useAccount } from "wagmi";
import { vaultsContractConfig } from "@config/contractsConfig";
import { mainnet } from "viem/chains";
import { formatNumber, numberToCurrency } from "utils";
import useTokenBalance from "@hooks/useTokenBalance";
import { useQuery } from "@tanstack/react-query";
import { getRewardsPointdata } from "services";
import { AirdropVaultPageContext } from "@providers/AirdropVaultProvider";
// import { getTagBasedOnType } from "@components/Sidebar/Sidebar";
// import { SidebarTag } from "@constants/common";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";
import useEthConversion from "@hooks/useEthConversion";
import { TMarkerPoint } from "@components/Charts/configs";
import { Tab } from "@kelp-dao/kernel-design-system";
import APR_REPORT_AGETH from "@files/APR_REPORT_AGETH.json";
import APR_REPORT_SBUSD from "@files/APR_REPORT_SBUSD.json";
import APR_REPORT_HGETH from "@files/APR_REPORT_HGETH.json";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useVaultPerformance from "@hooks/useVaultPerformance";
import useSbUsdExchangeRate from "@hooks/useSbUsdExchangeRate";
import useHgEthExchangeRate from "@hooks/useHgEthExchangeRate";
import { TimeframeOption } from "@constants/types";
import OverviewV2 from "./VaultOverview";
import { trackEvent } from "utils";
import { VAULT_PORTFOLIO_TAB_CHANGE } from "@constants/analytics";

interface PortfolioProps {
  config: {
    tabs: Array<{ key: string; title: string; isNew?: boolean }>;
    points: {
      walletConnect: string;
      supported: Array<any>;
    };
    overviewData: any;
    aprReports: any;
  };
  constants: any;
  vaultType: string;
  xTokenSymbol: string;
  asset: string;
  // Optional claims hook - returns claim data array
  useClaimsHook?: () => any[];
  // Optional APR tooltip text
  aprTooltip?: string;
}

// Vault-specific marker points
const getMarkerPoints = (asset: string): TMarkerPoint[] => {
  switch (asset) {
    case "ageth":
      return [
        {
          label: "2024-10-22",
          count: 1,
          data: [
            {
              event: "agETH users received Scroll Airdrop.",
              timestamp: "2024/10/22",
            },
          ],
        },
        {
          label: "2025-04-15",
          count: 1,
          data: [
            {
              event: "agETH users received the Kernel Airdrop",
              timestamp: "2025/04/14",
            },
          ],
        },
      ];
    case "hgeth":
      return [
        {
          label: "2025-01-12",
          count: 1,
          data: [
            {
              event: "hgETH vault launched",
              timestamp: "2025/01/12",
            },
          ],
        },
      ];
    case "sbusd":
    default:
      return []; // Stable vault doesn't have marker points
  }
};

const Portfolio = ({
  config,
  xTokenSymbol,
  asset,
  useClaimsHook,
  aprTooltip,
}: PortfolioProps) => {
  const { address } = useAccount();
  const { cmsAllocations } = useContext(AirdropVaultPageContext);
  const { chain } = useDappChain();
  const { yieldVaultEnabledChains, stableVaultEnabledChains } = useDappConfig();

  // Vault-specific hooks and data
  const {
    xTokenVaultBalance,
    xTokenStableVaultBalance,
    xTokenYieldVaultBalance,
  } = useTokenBalance();
  const agEthExchangeRate = useAgEthExchangeRate();
  const sbUsdExchangeRate = useSbUsdExchangeRate();
  const hgEthExchangeRate = useHgEthExchangeRate();

  // Get vault-specific data
  const vaultSpecificData = useMemo(() => {
    switch (asset) {
      case "sbusd":
        return {
          tokenBalance: xTokenStableVaultBalance,
          exchangeRate: sbUsdExchangeRate,
          enabledChains: stableVaultEnabledChains,
          aprReport: APR_REPORT_SBUSD,
          showDateSelector: true,
        };
      case "hgeth":
        return {
          tokenBalance: xTokenYieldVaultBalance,
          exchangeRate: hgEthExchangeRate,
          enabledChains: yieldVaultEnabledChains,
          aprReport: APR_REPORT_HGETH,
          showDateSelector: true,
        };
      case "ageth":
      default:
        return {
          tokenBalance: xTokenVaultBalance,
          exchangeRate: agEthExchangeRate,
          enabledChains: yieldVaultEnabledChains,
          aprReport: APR_REPORT_AGETH,
          showDateSelector: true,
        };
    }
  }, [
    asset,
    xTokenVaultBalance,
    xTokenStableVaultBalance,
    xTokenYieldVaultBalance,
    agEthExchangeRate,
    sbUsdExchangeRate,
    hgEthExchangeRate,
    yieldVaultEnabledChains,
    stableVaultEnabledChains,
  ]);

  const aprAllocation = useMemo(() => {
    return [
      ...(cmsAllocations?.allocations || []),
      ...(([
        ...(vaultSpecificData.aprReport?.allocations || []),
      ].reverse() as CMSAllocation[]) || []),
    ];
  }, [cmsAllocations?.allocations, vaultSpecificData.aprReport]);

  // Use provided claims hook or a noop hook that returns empty array
  const noopClaimsHook = () => [];
  const claimsHook = useClaimsHook || noopClaimsHook;
  const claims = claimsHook();

  const isChainEnabled = vaultSpecificData.enabledChains.some(
    (enabledChain) => enabledChain.id === chain.id
  );

  const tabsData = useMemo(() => {
    const shouldShowClaims =
      useClaimsHook && claims?.length > 0 && address && isChainEnabled;

    if (shouldShowClaims) {
      return [
        ...config.tabs,
        {
          key: "claims",
          title: "Claims",
        },
      ];
    }
    return [...config.tabs];
  }, [claims?.length, address, isChainEnabled, config.tabs, useClaimsHook]);

  const { valueInEth: erRsEthToEth } = useEthConversion(
    vaultSpecificData.exchangeRate.toERC20
  );

  const holdingValue = vaultSpecificData.tokenBalance
    ? formatNumber(Number(vaultSpecificData.tokenBalance), false, 4, true)
    : "0";

  const holdingValueInEth =
    erRsEthToEth !== "-" && vaultSpecificData.tokenBalance
      ? formatNumber(
          Number(vaultSpecificData.tokenBalance) * Number(erRsEthToEth),
          false,
          4,
          true
        )
      : "-";

  const { data: response, isLoading } = useQuery({
    queryKey: ["rewardsData", address],
    queryFn: () => getRewardsPointdata(address as string),
    enabled: !!address,
  });

  const getValue = useCallback(
    (key: string) => {
      let value;
      const vaultData =
        response?.data?.[
          vaultsContractConfig[mainnet.id].interactionPool.address
        ] || null;
      if (vaultData) {
        const { km, scrollPoints, karakPoints, lineaPoints, zircuitPoints } =
          vaultData;
        switch (key) {
          case "kernel": {
            value = km && !isNaN(km) ? km / 1000 : "-";
            break;
          }
          case "linea": {
            value = lineaPoints ?? "-";
            break;
          }
          case "scroll": {
            value = scrollPoints ?? "-";
            break;
          }
          case "karak": {
            value = karakPoints ?? "-";
            break;
          }
          case "zircuitPoints": {
            value = zircuitPoints ?? "-";
            break;
          }
        }
      }

      if (value && value !== "-") {
        const precision = +value > 1 ? 2 : 4;
        return numberToCurrency((+value).toFixed(precision), precision);
      }
      return "-";
    },
    [response]
  );

  const pointsData = useMemo(() => {
    if (!config.points.supported || config.points.supported.length === 0) {
      return null;
    }

    const supportedPoints = config.points.supported.map((item: any) => {
      const value = getValue(item.key);
      return {
        ...item,
        value,
        valueLoading: isLoading,
      };
    });

    return {
      supported: supportedPoints,
      walletConnect: config.points.walletConnect,
    };
  }, [isLoading, getValue, config.points]);

  // Map asset to TokenType for useVaultPerformance hook
  const getTokenType = (asset: string): "agETH" | "hgETH" | "sbUSD" => {
    switch (asset) {
      case "ageth":
        return "agETH";
      case "hgeth":
        return "hgETH";
      case "sbusd":
        return "sbUSD";
      default:
        return "agETH";
    }
  };

  const tokenType = getTokenType(asset);
  // Set default timeframe: 7D for sbUSD, 1M for others
  const defaultTimeframe: TimeframeOption = "1M";
  const [timeframe, setTimeframe] = useState<TimeframeOption>(defaultTimeframe);

  // Use a default value for unsupported assets to avoid conditional hook calls
  const { chartData } = useVaultPerformance(tokenType as any, timeframe);

  // Get vault-specific chart data and marker points
  const markerPointsToUse = useMemo(() => getMarkerPoints(asset), [asset]);
  const showTimeframeSelector = true;

  const handleTimeframeChange = useCallback((newTimeframe: TimeframeOption) => {
    setTimeframe(newTimeframe);
  }, []);

  const handleTabChange = useCallback(
    (newTab: string) => {
      setActiveTab(newTab);
      trackEvent(VAULT_PORTFOLIO_TAB_CHANGE, {
        vault_type: tokenType,
        new_tab: newTab,
      });
    },
    [tokenType]
  );
  // Kernel Design System Tabs
  const kernelTabs = tabsData.map((t) => ({ label: t.title, value: t.key }));
  const [activeTab, setActiveTab] = useState<string>(kernelTabs[0]?.value);

  const renderActiveTab = () => {
    // Prefer switching by index to keep compatibility with provided config ordering
    const activeIndex = kernelTabs.findIndex((t) => t.value === activeTab);
    // Claims tab can be appended conditionally; also support explicit key match
    const isClaims = activeTab === "claims";

    if (isClaims) {
      return <Claims claims={claims} />;
    }

    switch (activeIndex) {
      case 0:
        return (
          <OverviewV2
            data={config.overviewData}
            chartData={chartData}
            markerPoints={markerPointsToUse}
            showTimeframeSelector={showTimeframeSelector}
            onTimeframeChange={handleTimeframeChange}
            tokenType={tokenType}
            defaultTimeframe={defaultTimeframe}
            aprTooltip={aprTooltip}
          />
        );
      case 1:
        return (
          <VaultAprReports
            cmsAllocations={aprAllocation}
            showDateSelector={vaultSpecificData.showDateSelector}
            aprTitle={asset === "sbusd" ? "estimated" : undefined}
          />
        );
      case 2:
      default:
        return (
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-[40px] items-stretch pt-4">
            <div
              className={`w-full ${
                pointsData ? "lg:w-[45%]" : "tablet:w-full"
              }`}
            >
              <Holdings
                balance={holdingValue}
                balanceEth={holdingValueInEth}
                symbol={xTokenSymbol}
              />
            </div>
            {pointsData && (
              <div className="w-full lg:w-[50%]">
                <PointsV2 data={pointsData} />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col pt-2 pb-4 gap-6 rounded-xl bg-bg-card border border-border-default">
        <div className="px-4 sm:px-6 tablet:px-8 pt-3 tablet:pt-4">
          <div className="mb-0">
            <Tab
              tabs={kernelTabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              innerContainerClassName="gap-1"
            />
          </div>

          <div className="mt-2 tablet:mt-3">{renderActiveTab()}</div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
